import { useEffect, useMemo, useRef, useState } from 'react';
import type { Mention } from '../../hooks';
import type { CommentMention } from '../../containers/CommentList/CommentList.types';
import type { MentionMatch } from '../../hooks/mentions/useTextParsing';
import { dedupeCommentMentions } from './mapMentions';

interface UseInitialMentionsArgs {
  value: string;
  initialMentions: CommentMention[];
  extractMentions: (
    text: string,
    commentMentions?: CommentMention[]
  ) => MentionMatch[];
  onMentionsChange?: (mentions: CommentMention[]) => void;
}

export function useInitialMentions({
  value,
  initialMentions,
  extractMentions,
  onMentionsChange
}: UseInitialMentionsArgs) {
  const [initialParsedMentions, setInitialParsedMentions] = useState<Mention[]>(
    []
  );
  const initialMentionsRef = useRef<CommentMention[]>(initialMentions);

  useEffect(() => {
    if (
      initialMentions.length !== initialMentionsRef.current.length ||
      initialMentions.some(
        (m, i) =>
          m.uid !== initialMentionsRef.current[i]?.uid ||
          m.email !== initialMentionsRef.current[i]?.email
      )
    ) {
      initialMentionsRef.current = initialMentions;
    }
  }, [initialMentions]);

  useEffect(() => {
    if (!value) {
      setInitialParsedMentions([]);
      return;
    }

    if (initialMentionsRef.current.length > 0) {
      const mentionMatches = extractMentions(value, initialMentionsRef.current);
      const formatted: Mention[] = mentionMatches.map((match, index) => ({
        id: `initial-${index}`,
        start: match.startIndex,
        end: match.endIndex,
        display: match.mentionText,
        user: {
          id: index,
          username: match.username,
          email: match.email || '',
          name: match.name || match.username
        }
      }));
      setInitialParsedMentions(formatted);

      if (onMentionsChange && formatted.length > 0) {
        const commentMentions: CommentMention[] = formatted.map(m => ({
          uid: m.user.username,
          email: m.user.email,
          fullName: m.user.name
        }));
        // Deduplicate by uid to prevent duplicate mentions
        onMentionsChange(dedupeCommentMentions(commentMentions));
      }
    } else {
      setInitialParsedMentions([]);
    }
  }, [value, extractMentions, onMentionsChange]);

  return { initialParsedMentions } as const;
}
